/**
 * MappingGuidePreviewModal — right-side drawer showing the diff between
 * the uploaded Excel guide and the current recipe before the user confirms.
 *
 * Uses the project-wide SideDrawer primitive (same as SchemaPreviewDialog /
 * DataPreviewModal / NewTransformationWizard) for visual consistency:
 *   - full-width (min(92vw, 1440px)), no fixed-pixel size restriction
 *   - maximize / restore toggle in the header
 *   - footer row with Cancel + Apply buttons
 *
 * The inner layout mirrors the schema-sets preview side-panel:
 *   left sidebar  — grouped table list / change-type filter chips
 *   right content — scrollable changes table for the selected group
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AutoAwesome as AiIcon,
  CheckCircleOutline as ValidIcon,
  ErrorOutline as ErrorIcon,
  FileUpload as UploadIcon,
  TableChart as TableIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import SideDrawer from './common/SideDrawer';
import type {
  MappingGuideChangeItem,
  MappingGuidePreviewResponse,
} from '../services/batchApi';

// ─── Status / change-type colour config ─────────────────────────────────────

const STATUS_CFG = {
  valid: { label: '✓ Valid', color: '#10b981', Icon: ValidIcon },
  error: { label: '✗ Error', color: '#ef4444', Icon: ErrorIcon },
  warning: { label: '⚠ Warning', color: '#f59e0b', Icon: WarningIcon },
} as const;

const TYPE_CFG = {
  added: { label: 'Added', color: '#10b981', bg: alpha('#10b981', 0.1), Icon: ValidIcon },
  modified: { label: 'Modified', color: '#f59e0b', bg: alpha('#f59e0b', 0.1), Icon: WarningIcon },
  ai_regenerated: { label: 'AI Regenerated', color: '#7c3aed', bg: alpha('#7c3aed', 0.1), Icon: AiIcon },
} as const;

type ChangeType = keyof typeof TYPE_CFG;

function formatConfidence(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return `${Math.round(value * 100)}%`;
  }
  return String(value);
}

function DiffCell({
  before,
  after,
  monospace = false,
  isNewRow = false,
}: {
  before: string;
  after: string;
  monospace?: boolean;
  /** Added rows have no prior recipe value — show the new value only, not "— → value". */
  isNewRow?: boolean;
}) {
  // No prior value (—) with a new value below reads like a false "change" diff.
  const showNewValueOnly = isNewRow || (before === '—' && after !== '—');
  if (showNewValueOnly) {
    return (
      <Typography
        variant="body2"
        sx={{
          fontSize: 12,
          fontWeight: 500,
          fontFamily: monospace ? 'monospace' : undefined,
          color: after === '—' ? 'text.disabled' : 'text.primary',
        }}
      >
        {after}
      </Typography>
    );
  }
  if (before === after) {
    return (
      <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: monospace ? 'monospace' : undefined }}>
        —
      </Typography>
    );
  }
  return (
    <Stack spacing={0.25}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.disabled',
          fontFamily: monospace ? 'monospace' : undefined,
          fontSize: 10,
          textDecoration: 'line-through',
          wordBreak: 'break-all',
        }}
      >
        {before}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: 12,
          fontWeight: 500,
          fontFamily: monospace ? 'monospace' : undefined,
          color: after !== before ? 'text.primary' : 'text.disabled',
        }}
      >
        {after}
      </Typography>
    </Stack>
  );
}

// ─── Individual change row ───────────────────────────────────────────────────

interface ChangeRowProps {
  change: MappingGuideChangeItem;
  index: number;
  selected: boolean;
  onToggle: (rowId: string) => void;
}

const ChangeRow: React.FC<ChangeRowProps> = ({ change, index, selected, onToggle }) => {
  const statusCfg = STATUS_CFG[change.status] ?? STATUS_CFG.valid;
  const typeCfg = TYPE_CFG[change.change_type as ChangeType] ?? TYPE_CFG.modified;
  const isError = change.status === 'error';

  const beforeTransform = change.before.transformation || change.before.transformation_type || '—';
  const afterTransform = change.after.transformation || change.after.transformation_type || '—';
  const afterSource = [change.after.source_file, change.after.source_field].filter(Boolean).join('.');
  const beforeSource = [change.before.source_file, change.before.source_field].filter(Boolean).join('.');
  const beforeConfidence = formatConfidence(change.before.confidence);
  const afterConfidence = formatConfidence(change.after.confidence);
  const beforeExplanation = (change.before.explanation as string) || '—';
  const afterExplanation = (change.after.explanation as string) || '—';
  const confidenceHasError = change.errors.some((e) => e.toLowerCase().includes('confidence'));
  const isNewRow = change.change_type === 'added';

  return (
    <TableRow
      sx={{
        backgroundColor: isError
          ? alpha('#ef4444', 0.04)
          : index % 2 === 0
          ? 'transparent'
          : alpha('#f1f5f9', 0.5),
        opacity: !selected ? 0.65 : 1,
        '&:hover': {
          backgroundColor: isError ? alpha('#ef4444', 0.07) : alpha('#f1f5f9', 0.8),
          opacity: 1,
        },
      }}
    >
      {/* Checkbox */}
      <TableCell sx={{ width: 44, py: 0.5, px: 1 }}>
        <Checkbox
          size="small"
          checked={selected}
          onChange={() => onToggle(change.row_id)}
          disabled={isError}
          sx={{ p: 0.5 }}
        />
      </TableCell>

      {/* Target field */}
      <TableCell sx={{ minWidth: 140 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10, display: 'block', lineHeight: 1.2 }}>
          {change.target_table}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {change.target_field}
        </Typography>
      </TableCell>

      {/* Change type */}
      <TableCell sx={{ width: 140 }}>
        <Chip
          size="small"
          icon={<typeCfg.Icon sx={{ fontSize: 12 }} />}
          label={typeCfg.label}
          sx={{
            height: 22, fontSize: 11, fontWeight: 700,
            backgroundColor: typeCfg.bg, color: typeCfg.color,
            border: `1px solid ${alpha(typeCfg.color, 0.3)}`,
            '& .MuiChip-icon': { color: typeCfg.color },
          }}
        />
      </TableCell>

      {/* Source before → after */}
      <TableCell sx={{ minWidth: 160 }}>
        {beforeSource && beforeSource !== afterSource ? (
          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: 11, textDecoration: 'line-through' }}>
              {beforeSource}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 500 }}>
              {afterSource || '—'}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ fontSize: 12 }}>{afterSource || '—'}</Typography>
        )}
      </TableCell>

      {/* Transformation before → after */}
      <TableCell sx={{ minWidth: 180 }}>
        <DiffCell before={beforeTransform} after={afterTransform} monospace isNewRow={isNewRow} />
      </TableCell>

      {/* Confidence (0–1); invalid values surface as row errors */}
      <TableCell sx={{ minWidth: 90 }}>
        <Box sx={confidenceHasError ? { color: '#ef4444' } : undefined}>
          <DiffCell before={beforeConfidence} after={afterConfidence} isNewRow={isNewRow} />
        </Box>
      </TableCell>

      {/* Explanation (description) */}
      <TableCell sx={{ minWidth: 140 }}>
        <DiffCell before={beforeExplanation} after={afterExplanation} isNewRow={isNewRow} />
      </TableCell>

      {/* Request changes (AI annotation) */}
      <TableCell sx={{ minWidth: 130 }}>
        {change.request_changes ? (
          <Tooltip title={change.request_changes} arrow>
            <Typography
              variant="caption"
              sx={{
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                overflow: 'hidden', fontSize: 11, color: '#7c3aed', fontWeight: 500, lineHeight: 1.2,
              }}
            >
              {change.request_changes}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>
        )}
      </TableCell>

      {/* Status + inline errors */}
      <TableCell sx={{ minWidth: 130 }}>
        <Stack spacing={0.5} alignItems="flex-start">
          <Chip
            size="small"
            label={statusCfg.label}
            sx={{
              height: 22, fontSize: 11, fontWeight: 600,
              backgroundColor: alpha(statusCfg.color, 0.1),
              color: statusCfg.color,
              border: `1px solid ${alpha(statusCfg.color, 0.3)}`,
            }}
          />
          {change.errors.map((err, i) => (
            <Tooltip key={i} title={err} arrow>
              <Typography
                variant="caption"
                sx={{
                  fontSize: 10, color: isError ? '#ef4444' : '#f59e0b',
                  display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden', maxWidth: 200,
                }}
              >
                {err}
              </Typography>
            </Tooltip>
          ))}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

// ─── Left sidebar ────────────────────────────────────────────────────────────

interface SidebarProps {
  preview: MappingGuidePreviewResponse;
  /** 'all' | table name */
  selectedTable: string;
  onSelectTable: (table: string) => void;
  selectedRowIds: Set<string>;
}

const Sidebar: React.FC<SidebarProps> = ({ preview, selectedTable, onSelectTable, selectedRowIds }) => {
  // Group change counts by target_table
  const tableGroups = useMemo(() => {
    const map: Record<string, { total: number; errors: number }> = {};
    for (const c of preview.changes) {
      if (!map[c.target_table]) map[c.target_table] = { total: 0, errors: 0 };
      map[c.target_table].total += 1;
      if (c.status === 'error') map[c.target_table].errors += 1;
    }
    return map;
  }, [preview]);

  const tables = Object.keys(tableGroups).sort();

  const selectedCount = selectedRowIds.size;

  return (
    <Box
      sx={{
        width: 220,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Summary header */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.06em' }}>
          Tables
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: 10, mt: 0.25 }}>
          {selectedCount} of {preview.changes.filter(c => c.status !== 'error').length} changes selected
        </Typography>
      </Box>

      {/* Table list */}
      <List disablePadding sx={{ flex: 1, overflow: 'auto' }}>
        {/* All */}
        <ListItemButton
          selected={selectedTable === 'all'}
          onClick={() => onSelectTable('all')}
          sx={{
            py: 0.75, px: 1.5,
            '&.Mui-selected': { backgroundColor: alpha('#3b82f6', 0.08), borderLeft: '3px solid', borderColor: 'primary.main' },
            '&.Mui-selected:hover': { backgroundColor: alpha('#3b82f6', 0.12) },
          }}
        >
          <TableIcon sx={{ fontSize: 16, mr: 1, color: selectedTable === 'all' ? 'primary.main' : 'text.secondary' }} />
          <ListItemText
            primary="All tables"
            secondary={`${preview.total_changes} changes`}
            primaryTypographyProps={{ fontSize: 13, fontWeight: selectedTable === 'all' ? 600 : 400 }}
            secondaryTypographyProps={{ fontSize: 11 }}
          />
          {preview.error_count > 0 && (
            <Chip size="small" label={preview.error_count} color="error" sx={{ height: 18, fontSize: 10, ml: 0.5 }} />
          )}
        </ListItemButton>

        <Divider />

        {tables.map((table) => {
          const g = tableGroups[table];
          return (
            <ListItemButton
              key={table}
              selected={selectedTable === table}
              onClick={() => onSelectTable(table)}
              sx={{
                py: 0.75, px: 1.5,
                '&.Mui-selected': { backgroundColor: alpha('#3b82f6', 0.08), borderLeft: '3px solid', borderColor: 'primary.main' },
                '&.Mui-selected:hover': { backgroundColor: alpha('#3b82f6', 0.12) },
              }}
            >
              <ListItemText
                primary={table}
                secondary={`${g.total} change${g.total !== 1 ? 's' : ''}`}
                primaryTypographyProps={{ fontSize: 12, fontWeight: selectedTable === table ? 600 : 400, noWrap: true }}
                secondaryTypographyProps={{ fontSize: 11 }}
              />
              {g.errors > 0 && (
                <Chip size="small" label={g.errors} color="error" sx={{ height: 18, fontSize: 10, ml: 0.5, flexShrink: 0 }} />
              )}
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

export interface MappingGuidePreviewModalProps {
  open: boolean;
  preview: MappingGuidePreviewResponse | null;
  onCancel: () => void;
  onApply: (preview: MappingGuidePreviewResponse) => Promise<void>;
  progressText?: string;
}

const MappingGuidePreviewModal: React.FC<MappingGuidePreviewModalProps> = ({
  open,
  preview,
  onCancel,
  onApply,
  progressText,
}) => {
  const [applying, setApplying] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [selectedTable, setSelectedTable] = useState<string>('all');

  // Initialise selection when preview arrives — pre-select all non-error rows.
  React.useEffect(() => {
    if (preview) {
      setSelectedRowIds(new Set(preview.changes.filter(c => c.status !== 'error').map(c => c.row_id)));
      setSelectedTable('all');
    } else {
      setSelectedRowIds(new Set());
    }
  }, [preview]);

  const handleToggleRow = useCallback((rowId: string) => {
    setSelectedRowIds(prev => {
      const next = new Set(prev);
      next.has(rowId) ? next.delete(rowId) : next.add(rowId);
      return next;
    });
  }, []);

  // Rows visible in the right panel (filtered by selected table)
  const visibleChanges = useMemo(() => {
    if (!preview) return [];
    return selectedTable === 'all'
      ? preview.changes
      : preview.changes.filter(c => c.target_table === selectedTable);
  }, [preview, selectedTable]);

  const visibleNonErrorChanges = useMemo(() => visibleChanges.filter(c => c.status !== 'error'), [visibleChanges]);
  const allVisibleSelected = visibleNonErrorChanges.length > 0 && visibleNonErrorChanges.every(c => selectedRowIds.has(c.row_id));
  const someVisibleSelected = visibleNonErrorChanges.some(c => selectedRowIds.has(c.row_id));

  const handleToggleAll = useCallback(() => {
    setSelectedRowIds(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleNonErrorChanges.forEach(c => next.delete(c.row_id));
      } else {
        visibleNonErrorChanges.forEach(c => next.add(c.row_id));
      }
      return next;
    });
  }, [allVisibleSelected, visibleNonErrorChanges]);

  const selectedChanges = useMemo(() =>
    preview?.changes.filter(c => selectedRowIds.has(c.row_id)) ?? [],
    [preview, selectedRowIds]
  );

  const selectedErrorCount = useMemo(() => selectedChanges.filter(c => c.status === 'error').length, [selectedChanges]);
  const selectedValidCount = useMemo(() => selectedChanges.filter(c => c.status !== 'error').length, [selectedChanges]);

  const hasErrors = (preview?.error_count ?? 0) > 0;
  const hasChanges = (preview?.total_changes ?? 0) > 0;

  const handleApply = useCallback(async () => {
    if (!preview) return;
    setApplying(true);
    try {
      await onApply({ ...preview, changes: selectedChanges });
    } finally {
      setApplying(false);
    }
  }, [preview, onApply, selectedChanges]);

  // ── Drawer title node (same style as SchemaPreviewDialog) ──
  const drawerTitle = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <UploadIcon color="primary" sx={{ fontSize: 20 }} />
      <Typography variant="h6" fontWeight={600} noWrap>
        Mapping Guide — Upload Preview
      </Typography>
      {preview && (
        <>
          <Chip
            label={`${preview.total_changes} change${preview.total_changes !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
          />
          {hasErrors && (
            <Chip label={`${preview.error_count} error${preview.error_count !== 1 ? 's' : ''}`} size="small" color="error" />
          )}
        </>
      )}
    </Stack>
  );

  // ── Footer (same right-aligned pattern as SchemaPreviewDialog) ──
  const footer = hasChanges ? (
    <>
      {selectedErrorCount > 0 && (
        <Typography variant="caption" color="error" sx={{ mr: 'auto', fontSize: 11, fontWeight: 600 }}>
          Uncheck error rows to proceed with partial application.
        </Typography>
      )}
      {selectedErrorCount === 0 && selectedChanges.length === 0 && (
        <Typography variant="caption" color="warning.main" sx={{ mr: 'auto', fontSize: 11 }}>
          Select at least one change to apply.
        </Typography>
      )}
      <Button
        variant="contained"
        onClick={handleApply}
        disabled={applying || selectedErrorCount > 0 || selectedChanges.length === 0}
        startIcon={applying ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
        sx={{ textTransform: 'none', minWidth: 150 }}
      >
        {applying ? 'Applying…' : `Apply ${selectedValidCount} Change${selectedValidCount !== 1 ? 's' : ''}`}
      </Button>
    </>
  ) : null;

  return (
    <SideDrawer
      open={open}
      onClose={onCancel}
      title={drawerTitle}
      footer={footer}
      disableClose={applying}
    >
      {/* ── Loading state (same pattern as GenerateMappingDialog step 1) ── */}
      {!preview ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, py: 6, px: 3, textAlign: 'center' }}>
          <CircularProgress size={36} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {progressText || 'Loading preview…'}
          </Typography>
        </Box>
      ) : !hasChanges ? (
        /* ── No changes ── */
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 6 }}>
          <Stack alignItems="center" spacing={1}>
            <ValidIcon sx={{ fontSize: 40, color: '#10b981' }} />
            <Typography variant="body2" color="text.secondary">
              No changes detected — the uploaded file matches the current recipe exactly.
            </Typography>
          </Stack>
        </Box>
      ) : (
        /* ── Summary alert + split layout ── */
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {/* Summary alert */}
          <Alert
            severity={hasErrors ? 'error' : (preview.warning_count ?? 0) > 0 ? 'warning' : 'success'}
            sx={{ borderRadius: 0, py: 0.75, px: 2, fontSize: 13, flexShrink: 0 }}
          >
            {`${preview.total_changes} change${preview.total_changes !== 1 ? 's' : ''} detected — `}
            {`${selectedValidCount} valid selected`}
            {hasErrors && `. Fix or skip ${preview.error_count} error${preview.error_count !== 1 ? 's' : ''} to apply.`}
          </Alert>

          {/* Split layout: sidebar + table */}
          <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {/* Left sidebar */}
            <Sidebar
              preview={preview}
              selectedTable={selectedTable}
              onSelectTable={setSelectedTable}
              selectedRowIds={selectedRowIds}
            />

            {/* Right changes table */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        padding="checkbox"
                        sx={{
                          width: 44, py: 1, px: 1,
                          backgroundColor: '#f8fafc',
                          borderBottom: '2px solid', borderColor: 'divider',
                        }}
                      >
                        <Checkbox
                          size="small"
                          indeterminate={someVisibleSelected && !allVisibleSelected}
                          checked={allVisibleSelected}
                          onChange={handleToggleAll}
                          disabled={visibleNonErrorChanges.length === 0}
                        />
                      </TableCell>
                      {[
                        { label: 'Target Field', minWidth: 140 },
                        { label: 'Type', width: 140 },
                        { label: 'Source', minWidth: 160 },
                        { label: 'Transformation', minWidth: 180 },
                        { label: 'Confidence', minWidth: 90 },
                        { label: 'Explanation', minWidth: 140 },
                        { label: 'Request Changes', minWidth: 130 },
                        { label: 'Status / Errors', minWidth: 130 },
                      ].map((col) => (
                        <TableCell
                          key={col.label}
                          sx={{
                            minWidth: col.minWidth,
                            width: (col as any).width,
                            fontWeight: 700, fontSize: 11,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            py: 1,
                            backgroundColor: '#f8fafc',
                            borderBottom: '2px solid', borderColor: 'divider',
                          }}
                        >
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleChanges.map((change, idx) => (
                      <ChangeRow
                        key={change.row_id}
                        change={change}
                        index={idx}
                        selected={selectedRowIds.has(change.row_id)}
                        onToggle={handleToggleRow}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Box>
      )}
    </SideDrawer>
  );
};

export default MappingGuidePreviewModal;
